import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../config/supabase';
import { FiEye, FiEyeOff, FiArrowLeft, FiMail, FiRefreshCw } from 'react-icons/fi';

// ─── OTP Code Input ───────────────────────────────────────────────────────────
const OtpInput = ({ onComplete, onReset, onChange }) => {
    const [digits, setDigits] = useState(['', '', '', '', '', '']);
    const refs = useRef([]);

    const setAll = (next) => {
        setDigits(next);
        onChange?.(next.join(''));
    };

    const update = (idx, val) => {
        if (!/^\d*$/.test(val)) return;
        const next = [...digits];
        next[idx] = val.slice(-1);
        setAll(next);
        if (val && idx < 5) refs.current[idx + 1]?.focus();
        const full = next.join('');
        if (full.length === 6) onComplete?.(full);
    };

    const handleKeyDown = (idx, e) => {
        if (e.key === 'Backspace') {
            if (digits[idx]) {
                const next = [...digits];
                next[idx] = '';
                setAll(next);
            } else if (idx > 0) {
                refs.current[idx - 1]?.focus();
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pasted.length === 6) {
            setAll(pasted.split(''));
            refs.current[5]?.focus();
            onComplete?.(pasted);
        }
    };

    // Expose reset to parent
    useEffect(() => {
        onReset(() => {
            setDigits(['', '', '', '', '', '']);
            onChange?.('');
        });
    }, [onReset, onChange]);

    return (
        <div className="flex items-center justify-center gap-3">
            {digits.map((d, i) => (
                <input
                    key={i}
                    ref={el => refs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={e => update(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                    className="w-12 h-14 text-center text-xl font-bold bg-gray-50 border-0 rounded-2xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all select-none"
                />
            ))}
        </div>
    );
};

// ─── Resend countdown ─────────────────────────────────────────────────────────
const ResendTimer = ({ onResend, isResending }) => {
    const [seconds, setSeconds] = useState(60);

    useEffect(() => {
        if (seconds <= 0) return;
        const t = setTimeout(() => setSeconds(s => s - 1), 1000);
        return () => clearTimeout(t);
    }, [seconds]);

    const handleResend = async () => {
        await onResend();
        setSeconds(60);
    };

    return seconds > 0 ? (
        <p className="text-center text-sm text-gray-400">
            Resend code in <span className="font-semibold text-gray-600">{seconds}s</span>
        </p>
    ) : (
        <button
            type="button"
            onClick={handleResend}
            disabled={isResending}
            className="flex items-center justify-center gap-1.5 w-full text-sm text-purple-600 hover:text-purple-700 font-medium disabled:opacity-50"
        >
            <FiRefreshCw className={`w-3.5 h-3.5 ${isResending ? 'animate-spin' : ''}`} />
            {isResending ? 'Sending…' : 'Resend code'}
        </button>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────
const Login = () => {
    const { user, signIn, userProfile, company, refreshProfile } = useAuth();

    // view: 'login' | 'register' | 'verify'
    const [view, setView] = useState('login');

    // ── Login state ──
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [showLoginPw, setShowLoginPw] = useState(false);
    const [loginError, setLoginError] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);

    // ── Register state ──
    const [reg, setReg] = useState({ fullName: '', username: '', email: '', password: '', confirmPassword: '' });
    const [showRegPw, setShowRegPw] = useState(false);
    const [showConfirmPw, setShowConfirmPw] = useState(false);
    const [regError, setRegError] = useState('');
    const [regLoading, setRegLoading] = useState(false);

    // ── Verify state ──
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [verifyError, setVerifyError] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [otpResetFn, setOtpResetFn] = useState(null);

    // ── Password recovery state ──
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotError, setForgotError] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [resetPw, setResetPw] = useState('');
    const [resetConfirmPw, setResetConfirmPw] = useState('');
    const [showResetPw, setShowResetPw] = useState(false);
    const [resetError, setResetError] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetResendLoading, setResetResendLoading] = useState(false);
    const [resetOtpResetFn, setResetOtpResetFn] = useState(null);
    const [loginNotice, setLoginNotice] = useState('');
    // The recovery code is single-use: once verifyOtp succeeds we hold a
    // recovery session, so a failed password update must retry updateUser
    // only, not re-verify a code that is already consumed.
    const recoveryVerifiedRef = useRef(false);

    const [authSettling, setAuthSettling] = useState(false);
    // Stored for verify step
    const pendingRef = useRef({ mode: '', email: '', password: '', fullName: '', username: '' });

    // Stable callback passed to OtpInput so it can give us a reset handle
    const handleOtpReset = useCallback(fn => setOtpResetFn(() => fn), []);
    const handleResetOtpReset = useCallback(fn => setResetOtpResetFn(() => fn), []);

    // ── Redirects ──
    if (authSettling) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-14 w-14 border-4 border-purple-500 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Finishing account setup...</p>
                </div>
            </div>
        );
    }

    // verifyOtp({ type: 'recovery' }) signs the user in, and AuthContext then
    // loads their profile — don't let that bounce them to the dashboard while
    // the code is being checked or before the new password is actually set. `user` clears synchronously on sign-out
    // while the profile clears a tick later, so checking it avoids a stale redirect.
    if (user && userProfile && company && view !== 'reset' && view !== 'new-password') {
        if (company.settings?.is_sandbox) return <Navigate to={`/${company.slug}/dashboard`} replace />;
        if (userProfile?.role === "driver") return <Navigate to={`/${company.slug}/my-deliveries`} replace />;
        return <Navigate to={`/${company.slug}/dashboard`} replace />;
    }

    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const fetchProfileDirect = async (userId) => {
        const { data, error } = await supabase
            .from('users')
            .select(`
            *,
            companies (
                id,
                name,
                slug,
                logo_url,
                primary_color,
                secondary_color,
                timezone,
                settings,
                subscription_plan,
                subscription_status,
                is_active,
                enterprise_logo_url,
                enterprise_icon_url,
                app_version
            )
        `)
            .eq('id', userId)
            .maybeSingle();

        if (error) {
            throw error;
        }

        return data;
    };

    const waitForProfileAndRedirect = async (userId) => {
        let attempts = 0;

        while (attempts < 30) {
            await sleep(500);

            const profile = await fetchProfileDirect(userId);
            const slug = profile?.companies?.slug;

            if (slug) {
                // Let auth/session/guards settle before moving
                await sleep(1500);
                window.location.replace(`/${slug}/dashboard`);
                return true;
            }

            attempts++;
        }

        return false;
    };

    // ─── Login submit ──────────────────────────────────────────────────────────
    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        setLoginNotice('');
        setLoginLoading(true);

        try {
            const { error } = await signIn(loginEmail, loginPassword);

            if (error) {
                const msg = error.message?.toLowerCase() || '';

                // 🔥 KEY PART: detect unconfirmed email
                if (msg.includes('email not confirmed') || msg.includes('not confirmed')) {

                    // Store email for OTP screen
                    pendingRef.current = {
                        mode: 'login_unconfirmed',
                        email: loginEmail,
                        password: loginPassword,
                        fullName: '',
                        username: '',
                    };

                    // Switch to OTP view
                    setView('verify');

                    // Send confirmation email again
                    await supabase.auth.resend({
                        type: 'signup',
                        email: loginEmail,
                    });

                    return;
                }

                setLoginError(error.message);
            }
        } catch {
            setLoginError('An unexpected error occurred. Please try again.');
        } finally {
            setLoginLoading(false);
        }
    };

    // ─── Register submit ───────────────────────────────────────────────────────
    const handleRegister = async (e) => {
        e.preventDefault();
        setRegError('');

        const { fullName, username, email, password, confirmPassword } = reg;

        if (fullName.trim().length < 2) return setRegError('Full name must be at least 2 characters.');
        if (!/^[a-zA-Z0-9_]{3,30}$/.test(username)) return setRegError('Username must be 3–30 characters (letters, numbers, underscores only).');
        if (password.length < 8) return setRegError('Password must be at least 8 characters.');
        if (password !== confirmPassword) return setRegError('Passwords do not match.');

        setRegLoading(true);
        try {
            const { data, error } = await supabase.auth.signUp({ email, password });
            if (error) {
                setRegError(error.message);
                return;
            }

            // If session is returned immediately, email confirmation is disabled
            if (data.session) {
                // Auto-confirm path: skip OTP, set up sandbox directly
                await setupSandbox(fullName.trim(), username.trim());
                return;
            }

            // Email confirmation required → move to OTP step
            pendingRef.current = { mode: 'register', email, password, fullName: fullName.trim(), username: username.trim() };
            setView('verify');
        } catch {
            setRegError('An unexpected error occurred. Please try again.');
        } finally {
            setRegLoading(false);
        }
    };

    // ─── OTP verify ───────────────────────────────────────────────────────────
    const handleVerify = async (code) => {
        if (code.length !== 6) return;

        setVerifyError('');
        setVerifyLoading(true);

        try {
            const { data, error } = await supabase.auth.verifyOtp({
                email: pendingRef.current.email,
                token: code,
                type: 'signup',
            });

            if (error) {
                setVerifyError('Invalid or expired code. Please try again.');
                otpResetFn?.();
                setVerifyLoading(false);
                return;
            }

            const verifiedUserId = data?.user?.id || data?.session?.user?.id;

            if (!verifiedUserId) {
                setVerifyError('Verification succeeded, but user session was not ready. Please try again.');
                setVerifyLoading(false);
                return;
            }

            setAuthSettling(true);

            if (pendingRef.current.mode === 'register') {
                await setupSandbox(
                    pendingRef.current.fullName,
                    pendingRef.current.username,
                    verifiedUserId
                );
                return;
            }

            if (pendingRef.current.mode === 'login_unconfirmed') {
                const redirected = await waitForProfileAndRedirect(verifiedUserId);

                if (!redirected) {
                    setVerifyError('Account verified, but workspace is not ready yet. Please refresh the page.');
                    setVerifyLoading(false);
                    setAuthSettling(false);
                }

                return;
            }

            setVerifyError('Unknown verification flow. Please try again.');
            setVerifyLoading(false);
            setAuthSettling(false);
        } catch (err) {
            console.error('Verification failed:', err);
            setVerifyError('Verification failed. Please try again.');
            setVerifyLoading(false);
            setAuthSettling(false);
        }
    };

    // ─── Create sandbox company + user profile ─────────────────────────────────
    const setupSandbox = async (fullName, username, verifiedUserId) => {
        try {
            setAuthSettling(true);
            setVerifyLoading(true);
            setVerifyError('');

            const { error: rpcError } = await supabase.rpc('setup_sandbox_account', {
                p_username: username,
                p_full_name: fullName,
            });

            if (rpcError && !rpcError.message?.includes('already exists')) {
                console.error('Sandbox setup error:', rpcError);
                setVerifyError('Account setup failed. Please contact support.');
                setVerifyLoading(false);
                setAuthSettling(false);
                return;
            }

            // Give backend a moment to insert/create the row
            await sleep(1200);

            const redirected = await waitForProfileAndRedirect(verifiedUserId);

            if (!redirected) {
                setVerifyError('Account created, but workspace is not ready yet. Please refresh the page.');
                setVerifyLoading(false);
                setAuthSettling(false);
            }
        } catch (error) {
            console.error('Setup sandbox failed:', error);
            setVerifyError('Account setup failed. Please try again.');
            setVerifyLoading(false);
            setAuthSettling(false);
        }
    };

    // ─── Resend OTP ────────────────────────────────────────────────────────────
    const handleResend = async () => {
        setResendLoading(true);
        setVerifyError('');
        try {
            await supabase.auth.resend({ type: 'signup', email: pendingRef.current.email });
            otpResetFn?.();
        } catch {
            setVerifyError('Failed to resend code. Please try again.');
        } finally {
            setResendLoading(false);
        }
    };

    // ─── Password recovery ─────────────────────────────────────────────────────
    const isRateLimitError = (error) => {
        const msg = error?.message?.toLowerCase() || '';
        return error?.status === 429 || msg.includes('rate limit') || msg.includes('security purposes');
    };

    const openForgot = () => {
        setForgotEmail(loginEmail);
        setForgotError('');
        setLoginError('');
        setLoginNotice('');
        setView('forgot');
    };

    // Same response whether or not the email has an account, so this screen
    // can't be used to discover which emails are registered.
    const sendRecoveryCode = (email) => supabase.auth.resetPasswordForEmail(email);

    const handleForgotSubmit = async (e) => {
        e.preventDefault();
        setForgotError('');

        const email = forgotEmail.trim().toLowerCase();
        if (!email) return;

        setForgotLoading(true);
        try {
            const { error } = await sendRecoveryCode(email);
            if (error) {
                if (isRateLimitError(error)) {
                    setForgotError('Too many requests. Please wait a minute and try again.');
                    return;
                }
                // Anything else is swallowed on purpose (see above)
                console.error('Password recovery request failed:', error);
            }

            recoveryVerifiedRef.current = false;
            setResetEmail(email);
            setResetCode('');
            setResetPw('');
            setResetConfirmPw('');
            setResetError('');
            setView('reset');
        } catch {
            setForgotError('An unexpected error occurred. Please try again.');
        } finally {
            setForgotLoading(false);
        }
    };

    const handleResetResend = async () => {
        setResetResendLoading(true);
        setResetError('');
        try {
            const { error } = await sendRecoveryCode(resetEmail);
            if (error && isRateLimitError(error)) {
                setResetError('Too many requests. Please wait a minute and try again.');
            }
            resetOtpResetFn?.();
        } catch {
            setResetError('Failed to resend code. Please try again.');
        } finally {
            setResetResendLoading(false);
        }
    };

    const leaveReset = async () => {
        // On the new-password step we're holding a recovery session — drop it
        // so abandoning the flow doesn't leave the user signed in. The code is
        // single-use, so they go back to request a fresh one.
        if (recoveryVerifiedRef.current) {
            recoveryVerifiedRef.current = false;
            await supabase.auth.signOut({ scope: 'local' });
        }
        setResetCode('');
        setResetPw('');
        setResetConfirmPw('');
        setResetError('');
        setView('forgot');
    };

    // ── Step 1: check the code. Only a correct code reveals the password form.
    const handleResetVerify = async (code = resetCode) => {
        if (resetLoading) return;
        setResetError('');
        if (code.length !== 6) return setResetError('Enter the 6-digit code from your email.');

        setResetLoading(true);
        try {
            const { error } = await supabase.auth.verifyOtp({
                email: resetEmail,
                token: code,
                type: 'recovery',
            });

            if (error) {
                setResetError(isRateLimitError(error)
                    ? 'Too many attempts. Please wait a minute and try again.'
                    : 'Invalid or expired code. Please try again.');
                resetOtpResetFn?.();
                return;
            }

            recoveryVerifiedRef.current = true;
            setResetCode('');
            setView('new-password');
        } catch {
            setResetError('Verification failed. Please try again.');
        } finally {
            setResetLoading(false);
        }
    };

    // ── Step 2: set the new password using the recovery session from step 1.
    const handleNewPasswordSubmit = async (e) => {
        e.preventDefault();
        setResetError('');

        if (!recoveryVerifiedRef.current) return leaveReset();
        if (resetPw.length < 8) return setResetError('Password must be at least 8 characters.');
        if (resetPw !== resetConfirmPw) return setResetError('Passwords do not match.');

        setResetLoading(true);
        try {
            const { error: updateErr } = await supabase.auth.updateUser({ password: resetPw });
            if (updateErr) {
                // Still holding the recovery session — let them pick another password
                setResetError(updateErr.message || 'Could not update password. Please try again.');
                return;
            }

            // Revoke every session for this account (including any held by
            // whoever may have had the old password), then require a fresh sign-in.
            recoveryVerifiedRef.current = false;
            const { error: signOutErr } = await supabase.auth.signOut({ scope: 'global' });
            if (signOutErr) {
                console.error('Global sign-out after password reset failed:', signOutErr);
                await supabase.auth.signOut({ scope: 'local' });
            }

            setResetPw('');
            setResetConfirmPw('');
            setLoginEmail(resetEmail);
            setLoginPassword('');
            setLoginNotice('Your password has been updated. Please sign in with your new password.');
            setView('login');
        } catch {
            setResetError('An unexpected error occurred. Please try again.');
        } finally {
            setResetLoading(false);
        }
    };

    const updateReg = (field, value) => {
        setReg(prev => ({ ...prev, [field]: value }));
    };

    const registerInputClass =
        "block w-full min-h-14 px-6 py-4 bg-gray-50 border-0 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors text-base leading-5 appearance-none";


    // ─── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="mobile-login-page min-h-[97dvh] flex items-start sm:items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-8 relative overflow-x-hidden overflow-y-auto">
            {/* Background Video */}
            <div className="fixed inset-0 z-0 pointer-events-none bg-black">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    webkit-playsinline="true"
                    preload="auto"
                    poster={process.env.PUBLIC_URL + '/assets/login-simage2.png'}
                    className="absolute inset-0 w-full h-full object-cover opacity-90 sm:blur-2xl sm:scale-[1.2]"
                >
                    <source src={process.env.PUBLIC_URL + '/assets/login-background.mp4'} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-black/60" />
            </div>

            {/* Card */}
            <div
                className="flex w-full max-w-6xl my-auto bg-white rounded-[20px] shadow-2xl overflow-hidden relative z-10"
                style={{
                    height: 'min(700px, calc(100dvh - 32px))',
                    maxHeight: 'calc(100dvh - 32px)',
                }}
            >

                {/* Left — illustration (hidden on mobile) */}
                <div className="hidden lg:flex lg:w-1/2 p-3 flex-shrink-0">
                    <div className="w-full h-full rounded-[16px] flex items-center justify-center relative overflow-hidden">
                        <img className="w-full h-full object-cover" src={process.env.PUBLIC_URL + '/assets/login-simage2.png'} alt="" />
                    </div>
                </div>

                {/* Right — form area */}
                <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-14">
                    <div className="w-full max-w-md">

                        {/* ── Login ─────────────────────────────── */}
                        {view === 'login' && (
                            <div className="space-y-10">
                                <div className="text-center space-y-2">
                                    <h1 className="text-4xl font-semibold text-gray-700">Hello Again!</h1>
                                    <p className="text-gray-500 text-lg">Welcome back, you've been missed!</p>
                                </div>

                                {loginError && (
                                    <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
                                        <p className="text-sm text-red-600 text-center">{loginError}</p>
                                    </div>
                                )}

                                {loginNotice && (
                                    <div className="p-4 bg-green-50 border border-green-200 rounded-2xl">
                                        <p className="text-sm text-green-700 text-center">{loginNotice}</p>
                                    </div>
                                )}

                                <form onSubmit={handleLogin} className="space-y-5">
                                    <input
                                        type="email"
                                        required
                                        autoComplete="email"
                                        className="block w-full px-6 py-5 bg-gray-50 border-0 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all text-base"
                                        placeholder="Enter email"
                                        value={loginEmail}
                                        onChange={e => setLoginEmail(e.target.value)}
                                        disabled={loginLoading}
                                    />

                                    <div className="relative">
                                        <input
                                            type={showLoginPw ? 'text' : 'password'}
                                            required
                                            autoComplete="current-password"
                                            className="block w-full px-6 py-5 bg-gray-50 border-0 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all text-base"
                                            placeholder="Password"
                                            value={loginPassword}
                                            onChange={e => setLoginPassword(e.target.value)}
                                            disabled={loginLoading}
                                        />
                                        <button type="button" onClick={() => setShowLoginPw(v => !v)} className="absolute inset-y-0 right-0 pr-6 flex items-center">
                                            {showLoginPw ? <FiEyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" /> : <FiEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />}
                                        </button>
                                    </div>

                                    <div className="text-right">
                                        <button type="button" onClick={openForgot} className="text-sm text-gray-500 hover:text-gray-700">Recovery Password</button>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loginLoading}
                                        className="w-full py-5 px-6 bg-gradient-to-r from-red-600 via-red-600 to-pink-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-base"
                                    >
                                        {loginLoading ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                Signing in…
                                            </span>
                                        ) : 'Sign in'}
                                    </button>

                                    <p className="text-center text-sm text-gray-500">
                                        Don't have an account?{' '}
                                        <button type="button" onClick={() => { setView('register'); setLoginError(''); }} className="text-purple-600 hover:text-purple-700 font-semibold">
                                            Create one
                                        </button>
                                    </p>
                                </form>
                            </div>
                        )}

                        {/* ── Register ──────────────────────────── */}
                        {view === 'register' && (
                            <div className="space-y-8">
                                <div className="text-center space-y-2">
                                    <h1 className="text-4xl font-semibold text-gray-700">Get Started</h1>
                                    <p className="text-gray-500 text-base">Create your free sandbox account</p>
                                </div>

                                {regError && (
                                    <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
                                        <p className="text-sm text-red-600 text-center">{regError}</p>
                                    </div>
                                )}

                                <form onSubmit={handleRegister} className="space-y-4">
                                    {/* Full Name */}
                                    <input
                                        type="text"
                                        required
                                        className={registerInputClass}
                                        placeholder="Full name"
                                        value={reg.fullName}
                                        onChange={e => updateReg('fullName', e.target.value)}
                                        disabled={regLoading}

                                        autoCapitalize="words"
                                        autoCorrect="off"
                                        spellCheck={false}
                                        autoComplete="name"
                                    />

                                    {/* Username */}
                                    <input
                                        type="text"
                                        required
                                        className={registerInputClass}
                                        placeholder="Username (e.g. john_doe)"
                                        value={reg.username}
                                        onChange={e => updateReg('username', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                                        disabled={regLoading}

                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        spellCheck={false}
                                        autoComplete="username"
                                    />

                                    {/* Email */}
                                    <input
                                        type="email"
                                        required
                                        autoComplete="email"
                                        className={registerInputClass}
                                        placeholder="Email address"
                                        value={reg.email}
                                        onChange={e => updateReg('email', e.target.value)}
                                        disabled={regLoading}

                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        spellCheck={false}
                                    />

                                    {/* Password */}
                                    <div className="relative">
                                        <input
                                            type={showRegPw ? 'text' : 'password'}
                                            required
                                            autoComplete="new-password"
                                            className={`${registerInputClass} pr-14`}
                                            placeholder="Password (min 8 characters)"
                                            value={reg.password}
                                            onChange={e => updateReg('password', e.target.value)}
                                            disabled={regLoading}

                                            autoCapitalize="none"
                                            autoCorrect="off"
                                            spellCheck={false}
                                        />
                                        <button type="button" onClick={() => setShowRegPw(v => !v)} className="absolute inset-y-0 right-0 pr-6 flex items-center">
                                            {showRegPw ? <FiEyeOff className="h-5 w-5 text-gray-400" /> : <FiEye className="h-5 w-5 text-gray-400" />}
                                        </button>
                                    </div>

                                    {/* Confirm Password */}
                                    <div className="relative">
                                        <input
                                            type={showConfirmPw ? 'text' : 'password'}
                                            required
                                            autoComplete="new-password"
                                            className={`${registerInputClass} pr-14`}
                                            placeholder="Confirm password"
                                            value={reg.confirmPassword}
                                            onChange={e => updateReg('confirmPassword', e.target.value)}
                                            disabled={regLoading}

                                            autoCapitalize="none"
                                            autoCorrect="off"
                                            spellCheck={false}
                                        />
                                        <button type="button" onClick={() => setShowConfirmPw(v => !v)} className="absolute inset-y-0 right-0 pr-6 flex items-center">
                                            {showConfirmPw ? <FiEyeOff className="h-5 w-5 text-gray-400" /> : <FiEye className="h-5 w-5 text-gray-400" />}
                                        </button>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={regLoading}
                                        className="w-full py-5 px-6 bg-gradient-to-r from-red-600 via-red-600 to-pink-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-base"
                                    >
                                        {regLoading ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                Creating account…
                                            </span>
                                        ) : 'Create account'}
                                    </button>

                                    <p className="text-center text-sm text-gray-500">
                                        Already have an account?{' '}
                                        <button type="button" onClick={() => { setView('login'); setRegError(''); }} className="text-purple-600 hover:text-purple-700 font-semibold">
                                            Sign in
                                        </button>
                                    </p>
                                </form>
                            </div>
                        )}

                        {/* ── Forgot password ───────────────────── */}
                        {view === 'forgot' && (
                            <div className="space-y-8">
                                <button
                                    type="button"
                                    onClick={() => { setView('login'); setForgotError(''); }}
                                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                                >
                                    <FiArrowLeft className="w-4 h-4" />
                                    Back to sign in
                                </button>

                                <div className="text-center space-y-2">
                                    <h1 className="text-4xl font-semibold text-gray-700">Reset Password</h1>
                                    <p className="text-gray-500 text-base">Enter your email and we'll send you a 6-digit code</p>
                                </div>

                                {forgotError && (
                                    <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
                                        <p className="text-sm text-red-600 text-center">{forgotError}</p>
                                    </div>
                                )}

                                <form onSubmit={handleForgotSubmit} className="space-y-5">
                                    <input
                                        type="email"
                                        required
                                        autoComplete="email"
                                        className="block w-full px-6 py-5 bg-gray-50 border-0 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all text-base"
                                        placeholder="Enter email"
                                        value={forgotEmail}
                                        onChange={e => setForgotEmail(e.target.value)}
                                        disabled={forgotLoading}
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        spellCheck={false}
                                    />

                                    <button
                                        type="submit"
                                        disabled={forgotLoading}
                                        className="w-full py-5 px-6 bg-gradient-to-r from-red-600 via-red-600 to-pink-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-base"
                                    >
                                        {forgotLoading ? 'Sending code…' : 'Send code'}
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* ── Reset password: step 1, enter code ── */}
                        {view === 'reset' && (
                            <div className="space-y-8">
                                <button
                                    type="button"
                                    onClick={leaveReset}
                                    disabled={resetLoading}
                                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
                                >
                                    <FiArrowLeft className="w-4 h-4" />
                                    Back
                                </button>

                                <div className="text-center space-y-3">
                                    <div className="flex justify-center">
                                        <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center">
                                            <FiMail className="w-8 h-8 text-purple-500" />
                                        </div>
                                    </div>
                                    <h1 className="text-3xl font-semibold text-gray-700">Check your email</h1>
                                    <p className="text-gray-500 text-sm leading-relaxed">
                                        If an account exists for<br />
                                        <span className="font-semibold text-gray-700">{resetEmail}</span><br />
                                        you'll receive a 6-digit code shortly.
                                    </p>
                                </div>

                                {resetError && (
                                    <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
                                        <p className="text-sm text-red-600 text-center">{resetError}</p>
                                    </div>
                                )}

                                <form
                                    onSubmit={e => { e.preventDefault(); handleResetVerify(); }}
                                    className="space-y-6"
                                >
                                    <OtpInput
                                        onChange={setResetCode}
                                        onComplete={handleResetVerify}
                                        onReset={handleResetOtpReset}
                                    />

                                    <button
                                        type="submit"
                                        disabled={resetLoading || resetCode.length !== 6}
                                        className="w-full py-5 px-6 bg-gradient-to-r from-red-600 via-red-600 to-pink-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-base"
                                    >
                                        {resetLoading ? 'Checking code…' : 'Verify code'}
                                    </button>
                                </form>

                                <ResendTimer onResend={handleResetResend} isResending={resetResendLoading} />

                                <p className="text-center text-xs text-gray-400 leading-relaxed">
                                    Didn't receive it? Check your spam folder.
                                </p>
                            </div>
                        )}

                        {/* ── Reset password: step 2, new password ─ */}
                        {view === 'new-password' && (
                            <div className="space-y-8">
                                <button
                                    type="button"
                                    onClick={leaveReset}
                                    disabled={resetLoading}
                                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
                                >
                                    <FiArrowLeft className="w-4 h-4" />
                                    Cancel
                                </button>

                                <div className="text-center space-y-2">
                                    <h1 className="text-4xl font-semibold text-gray-700">New Password</h1>
                                    <p className="text-gray-500 text-base">Choose a new password for <span className="font-semibold text-gray-700">{resetEmail}</span></p>
                                </div>

                                {resetError && (
                                    <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
                                        <p className="text-sm text-red-600 text-center">{resetError}</p>
                                    </div>
                                )}

                                <form onSubmit={handleNewPasswordSubmit} className="space-y-4">
                                    <div className="relative">
                                        <input
                                            type={showResetPw ? 'text' : 'password'}
                                            required
                                            autoFocus
                                            autoComplete="new-password"
                                            className={`${registerInputClass} pr-14`}
                                            placeholder="New password (min 8 characters)"
                                            value={resetPw}
                                            onChange={e => setResetPw(e.target.value)}
                                            disabled={resetLoading}
                                            autoCapitalize="none"
                                            autoCorrect="off"
                                            spellCheck={false}
                                        />
                                        <button type="button" onClick={() => setShowResetPw(v => !v)} className="absolute inset-y-0 right-0 pr-6 flex items-center">
                                            {showResetPw ? <FiEyeOff className="h-5 w-5 text-gray-400" /> : <FiEye className="h-5 w-5 text-gray-400" />}
                                        </button>
                                    </div>

                                    <input
                                        type={showResetPw ? 'text' : 'password'}
                                        required
                                        autoComplete="new-password"
                                        className={registerInputClass}
                                        placeholder="Confirm new password"
                                        value={resetConfirmPw}
                                        onChange={e => setResetConfirmPw(e.target.value)}
                                        disabled={resetLoading}
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        spellCheck={false}
                                    />

                                    <button
                                        type="submit"
                                        disabled={resetLoading}
                                        className="w-full py-5 px-6 bg-gradient-to-r from-red-600 via-red-600 to-pink-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-base"
                                    >
                                        {resetLoading ? 'Updating password…' : 'Update password'}
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* ── Verify ────────────────────────────── */}
                        {view === 'verify' && (
                            <div className="space-y-8">
                                {/* Back */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setView(pendingRef.current.mode === 'login_unconfirmed' ? 'login' : 'register');
                                        setVerifyError('');
                                    }}
                                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                                >
                                    <FiArrowLeft className="w-4 h-4" />
                                    Back
                                </button>

                                {/* Header */}
                                <div className="text-center space-y-3">
                                    <div className="flex justify-center">
                                        <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center">
                                            <FiMail className="w-8 h-8 text-purple-500" />
                                        </div>
                                    </div>
                                    <h1 className="text-3xl font-semibold text-gray-700">Check your email</h1>
                                    <p className="text-gray-500 text-sm leading-relaxed">
                                        We sent a 6-digit code to<br />
                                        <span className="font-semibold text-gray-700">{pendingRef.current.email}</span>
                                    </p>
                                </div>

                                {verifyError && (
                                    <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
                                        <p className="text-sm text-red-600 text-center">{verifyError}</p>
                                    </div>
                                )}

                                {/* OTP boxes */}
                                <div className="space-y-6">
                                    {verifyLoading ? (
                                        <div className="flex flex-col items-center gap-3 py-4">
                                            <svg className="animate-spin h-8 w-8 text-purple-500" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            <p className="text-sm text-gray-500">Setting up your account…</p>
                                        </div>
                                    ) : (
                                        <OtpInput
                                            onComplete={handleVerify}
                                            onReset={handleOtpReset}
                                        />
                                    )}

                                    <ResendTimer onResend={handleResend} isResending={resendLoading} />

                                    <p className="text-center text-xs text-gray-400 leading-relaxed">
                                        Didn't receive it? Check your spam folder.<br />
                                        The code expires in 60 minutes.
                                    </p>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
